import React, { Fragment, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import { Constants } from '../../Constants.ts';
import { ImageLoadEvent } from '../../Types/Events.ts';
import { resetFitAssistant } from '../../Thunks/Reset.ts';
import {
  CarInformationSelector,
  CurrentStepContentSelector,
  CurrentStepDataSelector,
  CurrentStepSelector,
  InitialDataSelector,
  ShowMoreInfoSelector,
  StepContentListSelector,
  StepDataListSelector,
  StepHasProductSearchSelector,
  TranslationsSelector,
} from '../../Selectors/FitAssistantSelectors';
import { fetchListOptions } from '../../Thunks/FetchListOptions.ts';
import { CarInformation, StepDataData } from '../../Types/FitAssistantData.ts';
import { MatchingProductsSelector } from '../../Selectors/MatchingProductsSelectors.ts';
import { addCarInformation, selectOption, setFooterIsFixed, unselectOptions } from '../../Slices/FitAssistantSlice';
import { getProducts } from '../../Thunks/Queries.ts';
import { ProductSearchType } from '../../Enums/ProductSearchType.ts';
import { FooterHelper } from '../../Helpers/MoreInfoHelper.ts';
import { trackChooseVariation, trackNoFit, trackOK } from '../../Analytics/Adobe.ts';
import Buttons from '../Buttons/Buttons.tsx';
import SecondaryButton from '../Buttons/SecondaryButton.tsx';
import VariationOption from './VariationOption.tsx';
import FitAssistantStep from '../../Enums/FitAssistantStep.ts';
import PrimaryButtonVariation from '../Buttons/PrimaryButtonVariation.tsx';
import FitAssistantVariation from '../../Enums/FitAssistantVariation.ts';
import { CloseType } from '../../Types/Tracking.ts';

import './VariationSelector.less';

const VariationSelector = () => {
  const [totalImageHeight, setTotalImageHeight] = useState<number>(0);

  const dispatch = useAppDispatch();
  const carInformation = useAppSelector(CarInformationSelector);
  const currentStep = useAppSelector(CurrentStepSelector);
  const currentStepData = useAppSelector(CurrentStepDataSelector);
  const currentStepContent = useAppSelector(CurrentStepContentSelector);
  const initialData = useAppSelector(InitialDataSelector);
  const matchingProducts = useAppSelector(MatchingProductsSelector);
  const showMoreInfo = useAppSelector(ShowMoreInfoSelector);
  const stepContent = useAppSelector(StepContentListSelector);
  const stepDataList = useAppSelector(StepDataListSelector);
  const stepHasProductSearch = useAppSelector(StepHasProductSearchSelector);
  const translations = useAppSelector(TranslationsSelector);

  useEffect(() => {
    document.addEventListener(Constants.Events.VARIATION_OPTION_IMAGE_LOAD, updateTotalImageHeight as EventListener);

    return () => {
      document.removeEventListener(
        Constants.Events.VARIATION_OPTION_IMAGE_LOAD,
        updateTotalImageHeight as EventListener
      );
    };
  }, []);

  useEffect(() => {
    let imageResult = FooterHelper.FooterShouldBeFixed('.variation-selector');
    dispatch(setFooterIsFixed(imageResult));
  }, [totalImageHeight, stepDataList]);

  const updateTotalImageHeight = (e: CustomEvent<ImageLoadEvent>): void => {
    setTotalImageHeight((current) => current + e.detail.height);
  };

  const getCarInformation = (option: StepDataData | undefined): CarInformation[] => {
    const filteredCarinformation = carInformation.filter((x) => x.parameterName != currentStep);
    const configItem = currentStepData?.configItems;

    if (!option) return filteredCarinformation;

    const variation: CarInformation = {
      parameterName: currentStep,
      configurationItem: configItem ?? '',
      text: option.EnglishText ?? option.Text ?? '',
      value: option.BedTypeLovId ?? option.Value,
    };

    let updCarInformation: CarInformation[] = [...filteredCarinformation, variation];
    return updCarInformation;
  };

  const handlePrimaryButtonClick = (): void => {
    let updatedCarInformation: CarInformation[] = [];

    if (!stepHasProductSearch) {
      dispatch(
        fetchListOptions({
          text: currentStepData?.data?.find((x) => x.Selected)?.Text ?? '',
          value: currentStepData?.data?.find((x) => x.Selected)?.Value ?? '',
          parameter: stepDataList[stepDataList.length - 1].parameters,
        })
      );
    } else {
      updatedCarInformation = setCompatibility();
    }

    trackOK(
      initialData.FitAssistantVariation,
      currentStep,
      initialData.IsYearFirst,
      updatedCarInformation ?? carInformation
    );
  };

  const setCompatibility = (): CarInformation[] => {
    const selectedOption = currentStepData?.data?.find((x) => x.Selected);
    const selectedValues = getCarInformation(selectedOption);

    dispatch(addCarInformation(selectedValues));

    dispatch(getProducts(ProductSearchType.VariationSelector));

    return selectedValues;
  };

  const handleOptionClick = (option: StepDataData, index: number): void => {
    if (!option.Selected) {
      let carInfo = getCarInformation(option);
      trackChooseVariation(initialData.FitAssistantVariation, initialData.IsYearFirst, carInfo);
      dispatch(selectOption(index));
    } else {
      dispatch(unselectOptions());
    }
  };

  const trackNoFitClick = (closeType: CloseType, clickLink?: string) => {
    trackNoFit(
      initialData.FitAssistantVariation,
      currentStep,
      initialData.IsYearFirst,
      carInformation,
      closeType,
      clickLink
    );
  };

  const onClickContent = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const linkElement = (e.target as HTMLElement).closest('a') as HTMLAnchorElement;
    if (linkElement) {
      trackNoFitClick('Read more', linkElement.href);
      window.location.href = linkElement.href;
    }
  };

  return (
    <>
      {!showMoreInfo && (
        <>
          <div className="variation-selector d-flex flex-column justify-content-center">
            <div className="variation-options">
              {currentStepData?.data?.map((option, index) => {
                if (
                  initialData.FitAssistantVariation === FitAssistantVariation.CarRoofRacks &&
                  (option.Value === 'roof-type-617945' || option.BedTypeLovId)
                ) {
                  return <Fragment key={index}></Fragment>;
                }
                return <VariationOption onClick={() => handleOptionClick(option, index)} option={option} key={index} />;
              })}
              {currentStepData?.data.length === 0 && (
                <>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: stepContent.find((x) => x.Parameter === FitAssistantStep.NoFits)?.Text ?? '',
                    }}
                  ></span>
                  {currentStepContent?.ResultContent && (
                    <div className="result-content mt-11">
                      <h5 className="mb-4">{translations.readMore}</h5>
                      <div className="items">
                        {currentStepContent?.ResultContent?.map((result, index) => (
                          <div key={index}>
                            <a href={result.Link} target="_blank" rel="noopener noreferrer" onClick={onClickContent}>
                              <img src={result.ImageUrl} alt={result.ImageAlt} className="mb-4" />
                              {result.LinkText}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <Buttons visible={!showMoreInfo}>
            <PrimaryButtonVariation text={translations.confirm} onClick={handlePrimaryButtonClick} />
            <SecondaryButton
              visible={(matchingProducts !== null && matchingProducts.length > 0) ?? false}
              text={translations.reset}
              onClick={() => dispatch(resetFitAssistant(false))}
            />
          </Buttons>
        </>
      )}
    </>
  );
};

export default VariationSelector;
